import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import ConfirmModal from '../components/shared/ConfirmModal';

const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: 'Are you sure?',
  message: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  variant: 'danger',
};

/**
 * Promise-based confirm for any page:
 *   const ok = await confirm({ title: 'Delete?', message: '…' });
 *   if (!ok) return;
 * Also accepts a string: await confirm('Delete this item?')
 */
export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return ctx;
};

export const ConfirmProvider = ({ children }) => {
  const [state, setState] = useState({
    isOpen: false,
    loading: false,
    ...DEFAULTS,
  });
  const resolveRef = useRef(null);

  const close = useCallback((result) => {
    setState((prev) => ({ ...prev, isOpen: false, loading: false }));
    const resolve = resolveRef.current;
    resolveRef.current = null;
    resolve?.(result);
  }, []);

  const confirm = useCallback((options = {}) => {
    const opts = typeof options === 'string' ? { message: options } : options || {};
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({
        isOpen: true,
        loading: false,
        title: opts.title ?? DEFAULTS.title,
        message: opts.message ?? DEFAULTS.message,
        confirmLabel: opts.confirmLabel ?? DEFAULTS.confirmLabel,
        cancelLabel: opts.cancelLabel ?? DEFAULTS.cancelLabel,
        variant: opts.variant ?? DEFAULTS.variant,
      });
    });
  }, []);

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <ConfirmModal
        isOpen={state.isOpen}
        title={state.title}
        message={state.message}
        confirmLabel={state.confirmLabel}
        cancelLabel={state.cancelLabel}
        variant={state.variant}
        loading={state.loading}
        onConfirm={() => close(true)}
        onCancel={() => close(false)}
      />
    </ConfirmContext.Provider>
  );
};

export default ConfirmContext;
